import { useEffect, useMemo, useState } from 'react';

export function useEditable<T>(initial: T, onSave: (next: T) => Promise<void> | void) {
  const [draft, setDraft] = useState<T>(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);

  const set = <K extends keyof T>(key: K, value: T[K]) => setDraft(d => ({ ...d, [key]: value }));
  const setNested = <K extends keyof T>(key: K, patch: Partial<T[K]>) =>
    setDraft(d => ({ ...d, [key]: { ...(d[key] as object), ...patch } as T[K] }));

  const reset = () => setDraft(initial);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  return { draft, set, setNested, dirty, saving, reset, save };
}
